// FAQ content for /faq. Draft copy seeded from existing site content
// (timelines, pricing model, data residency) — please review before launch.
// The Microsoft 365 Setup page keeps its own inline FAQ; this is separate.

export type FaqCategory = 'general' | 'services' | 'engagement' | 'microsoft';

export const FAQ_CATEGORIES: { id: FaqCategory; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'services', label: 'Services' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'microsoft', label: 'Microsoft & Data' },
];

export interface FaqItem {
  id: string;
  category: FaqCategory;
  question: string;
  answer: string;
  /** Optional follow-up link rendered after the answer. */
  link?: { label: string; to: string };
}

export const FAQS: FaqItem[] = [
  {
    id: 'who-we-are',
    category: 'general',
    question: 'Who is EightyThreeLabs?',
    answer:
      'A Microsoft-certified AI automation consultancy founded in 2024 in Toronto, built on 3+ years of hands-on experience in IT, software engineering, the Microsoft ecosystem, and modern automation platforms. We work directly with your team — no offshore handoffs.',
  },
  {
    id: 'who-we-serve',
    category: 'general',
    question: 'What size of company do you work with?',
    answer:
      'Both. Small and mid-sized businesses get fast, fixed-scope engagements that connect the SaaS tools they already pay for. Enterprise clients get governance, audit trails, and integration across their existing Microsoft estate and legacy systems.',
  },
  {
    id: 'where-based',
    category: 'general',
    question: 'Where are you located, and who do you work with?',
    answer: 'We are based in Toronto, Ontario, and serve clients across North America.',
  },
  {
    id: 'services-offered',
    category: 'services',
    question: 'What services do you actually deliver?',
    answer:
      'Three services: Copilot & Power Platform (custom Copilot agents, Power Apps, Power Automate), Automation Workflows (AI agents and workflow automation that connect the tools you already use), and Microsoft 365 Setup (tenant, email, Teams, and SharePoint, done for you). Scoping and readiness are part of every engagement, not a separate line item.',
  },
  {
    id: 'tools-used',
    category: 'services',
    question: 'What tools and platforms do you build with?',
    answer:
      'We are deep in the Microsoft ecosystem — Copilot Studio, Power Platform, Azure AI Services, Fabric — and fluent in open automation stacks like n8n and Make when they are the better fit. We choose the tool for the job, not the other way around.',
  },
  {
    id: 'not-just-microsoft',
    category: 'services',
    question: 'Do we have to be a Microsoft shop to work with you?',
    answer:
      'No. We are Microsoft-deep but tool-agnostic. If REST APIs, webhooks, and an open automation platform are the right fit for your stack, that is what we build with.',
  },
  {
    id: 'timeline',
    category: 'engagement',
    question: 'How long until we see something working?',
    answer:
      'Most engagements produce a first working automation in 2–6 weeks, depending on scope. Discovery and readiness work typically wraps in one to two weeks.',
  },
  {
    id: 'engagement-models',
    category: 'engagement',
    question: 'What engagement models do you offer?',
    answer:
      'Fixed-Scope Build, Build & Run, and Embedded Consultant. We scope the right model to your situation on a free initial call.',
  },
  {
    id: 'in-house-it',
    category: 'engagement',
    question: 'Do we need an in-house IT team to work with you?',
    answer:
      'No. For SMB engagements we can run the solution ourselves or hand it over fully documented — whichever you prefer. Enterprise engagements integrate with your existing IT and security teams.',
  },
  {
    id: 'pricing-model',
    category: 'engagement',
    question: 'How is pricing structured?',
    answer:
      'It depends on the engagement model. Fixed-scope builds are quoted up front; Build & Run and Embedded Consultant work is scoped around ongoing capacity. Book a free consultation and we will give you a straight answer, not a range that hides the real number.',
  },
  {
    id: 'data-residency',
    category: 'microsoft',
    question: 'Can you keep our data in Canada?',
    answer:
      'Yes. Self-hosted deployment options keep data within Canadian residency requirements, and we advise on tenant and licensing configuration to support that from day one.',
  },
  {
    id: 'security-compliance',
    category: 'microsoft',
    question: 'How do you handle security and compliance?',
    answer:
      'Security and governance are part of the initial design, not bolted on after launch — DLP policy, environment governance, SSO and role-based access, and audit trails are built into every solution we ship.',
  },
  {
    id: 'existing-licensing',
    category: 'microsoft',
    question: 'We already have Microsoft 365 — do we need new licensing?',
    answer:
      'Often not. Every engagement starts with a licensing and tenant review to see what your existing Microsoft 365 plan already supports before we recommend anything new.',
  },
  {
    id: 'm365-setup',
    category: 'microsoft',
    question: 'Do you also help with basic Microsoft 365 setup?',
    answer:
      'Yes — our Microsoft 365 Setup service gets small businesses and nonprofits onto professional email, Teams, and SharePoint for a one-time fee starting at $500 CAD. It’s separate from our AI automation work and a natural first step before it.',
    link: { label: 'See the Microsoft 365 Setup service', to: '/microsoft-365-setup' },
  },
];
